import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {truncateDecimals} from '../../Custom-Directives-Pipes/truncate-decimals.pipe';
import { CalcSettingsDialogComponent } from '../dialogs/calc-settings-dialog/calc-settings-dialog.component';
import { needsCropDefinition, needsStartingYear, routeForKind, SOIL_ANALYSIS_TYPE, soilKindFor, SoilRecommendationKind, usesLabSubmission } from "../utils/soil-recommendation-types";
import { CreateSoilCropNutritionComponent } from "../../soil-crop-nutrition/components/create-soil-crop-nutrition/create-soil-crop-nutrition.component";
import {CustomerModel} from "../../order-to-cash/models-otc/customer-model";
import {AnalysisType} from "../../models/analysisType";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatTableDataSource} from "@angular/material/table";
import {
  DraftConfirmationDialogComponent
} from "../../dialogs/draft-confirmation-dialog/draft-confirmation-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {Router} from "@angular/router";
import {InterpretationSampleRow} from '../models/interpretation-sample-row';
import {mergeHint} from '../../shared/sample-merge-text';
import {MergePanelGroup, recommendationMergePanelGroup} from '../../shared/merge-panel/merge-panel.model';
import {SeasonManagerService} from '../../services/season-manager.service';
import {NavBarComponent} from '../../nav-bar/nav-bar.component';
import {FieldDetailsService} from '../../services/field-details.service';
import {catchError, take} from 'rxjs/operators';
import {Subscription, throwError} from 'rxjs';
import {SeasonSharedDataService} from '../../services/season-shared-data.service';
import {PinManagerService} from '../../services/pin-manager.service';
import {RecommendationManagerService} from '../recommendation-manager.service';
import {CropModel} from '../models/crop';
import {RegionModel} from '../models/region';
import {GrowthStageModel} from '../models/growth-stage';
import {RecommendationManagerSharedDataService} from '../recommendation-manager-shared-data.service';
import {AgronimistModel} from '../../models/lab-manager/agronimist';
import {LabManagerService} from '../../lab-manager/lab-manager.service';
import {BasicLabManagerItemModel} from '../../models/lab-manager/basic-lab-manager-item';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LabSubmissionModel} from '../models/lab-submission';
import {SampleService} from '../../services/sample.service';

/**
 * Crop definitions for one season, with the season's own state so the picker can
 * say whether it has ended. seasonState is decided by the server.
 */
interface DefinitionSeasonGroup {
  seasonId: number;
  seasonName: string;
  seasonState: 'running' | 'upcoming' | 'ended' | null;
  seasonStart: string | null;
  seasonEnd: string | null;
  /** Resolved once at load. The template binds this, never a method call. */
  label: string;
  definitions: any[];
}

@Component({
    selector: 'app-create-recommendation',
    templateUrl: './create-recommendation.component.html',
    styleUrls: ['./create-recommendation.component.css'],
    standalone: false
})
export class CreateRecommendationComponent implements OnInit, OnDestroy {

  /**
   * Which soil recommendation the chosen Soil Type means.
   *
   * Resolved from the description, because the ids in rec_man_types are assigned by
   * hand and differ between environments, so nothing here may hardcode one. See
   * utils/soil-recommendation-types.
   */
  /**
   * The embedded SCN steps, when the chosen soil type is SCN.
   *
   * The Create button in this page's header drives it, because the button belongs to
   * the page and the fields and seasons belong to the component. Undefined whenever
   * SCN is not selected, so every use of it is optional-chained.
   */
  @ViewChild(CreateSoilCropNutritionComponent) scnSteps?: CreateSoilCropNutritionComponent;


  get soilKind(): SoilRecommendationKind {
    return soilKindFor(this.selectedAnalysisTypeId, this.soilTypes, this.selectedSoilTypeId);
  }

  /** SCN is built from fields and seasons, so it skips lab submission and samples. */
  get soilUsesLabSubmission(): boolean {
    return usesLabSubmission(this.soilKind);
  }

  get isScn(): boolean {
    return this.soilKind === 'scn';
  }

  /** The customer object the embedded SCN steps need, not just the id. */
  get selectedCustomer(): any {
    return this.filterCustomerList?.find(c => c.id === this.selectedClientId)
      ?? this.customerList?.find(c => c.id === this.selectedClientId)
      ?? null;
  }
  customerList: CustomerModel[] = [];
  currentPage: number = 0;
  pageSize: number = 25;
  startDate: Date = null;
  endDate: Date = null;
  searchText: string = '';
  totalElements: number = 0;
  selectedClientId: number = null;
  filteringData: boolean = false;
  dataSource: MatTableDataSource<InterpretationSampleRow> = null
  displayedColumns: string[] = [
    'checkbox',
    // First after the checkbox in all three variants. Somebody choosing samples
    // for a recommendation is often working from the bags in front of them.
    'bagNumber',
    'farm',
    'field',
    'fieldType',
    'sample',
    'labReference',
    'labName',
    'dateCreated',
    'crop',
    'cultivar',
    'growthStage'
  ];
  softTissueList: any[] = [];

  // 'definition' sits between crop and cultivar. Removing it from this array is
  // the whole rollback for the POC: the grid reverts to its current behaviour.
  softTissueColumns: string[] = [
    'checkbox', 'bagNumber', 'farm', 'field', 'fieldType', 'sample', 'labReference', 'labName', 'dateCreated', 'crop', 'definition', 'cultivar', 'growthStage'
  ];
  // Soil gets the same column. Nothing fails for an unlinked soil sample, but the
  // link is what lets Soil Maintenance use the cultivar's area rather than the
  // whole field's, and what would let SCN attribute soil data to one cultivar.
  soilColumns: string[] = [
    'checkbox', 'bagNumber', 'farm', 'field', 'sample', 'labReference', 'labName', 'dateCreated', 'definition', 'rockPct', 'depth'
  ];
  /**
   * Crop definitions for the whole grid, grouped by field then by season, built
   * once when a lab submission is chosen.
   *
   * Fully computed up front, deliberately. The template reads this map directly
   * and calls nothing: a method feeding *ngFor returns a new array on every
   * change detection pass, which Angular sees as changed data and re-renders,
   * which runs change detection again. That was an infinite loop.
   */
  definitionGroupsByField: { [fieldId: string]: DefinitionSeasonGroup[] } = {};
  definitionsLoading = false;
  definitionsFailed = false;
  linkingSampleId: number | null = null;

  selectedSamples: InterpretationSampleRow[] = [];
  selectedSampleCrop: {cropId: number, count: number}[] = [];
  labSubmissionList: LabSubmissionModel[] = [];
  selectedAnalysisTypeId: number = null;
  selectedLabSubmissionId: number = null;
  selectedSoilTypeId: number = null;
  soilTypes: {id: number, name: string}[] = [
    {id: 1, name: 'Soil Preparation'},
    {id: 2, name: 'Soil Maintenance'}
  ];
  filteredLabSubmissionList: LabSubmissionModel[] = [];
  clientFilterCtrl: FormControl = new FormControl();
  filterCustomerList: CustomerModel[] = [];
  isLoadingCustomers: boolean = true;
  cropList: GrowthStageModel[] = [];
  cropGrowthStageList: CropModel[] = [];
  growthStageList: GrowthStageModel[][] = [];
  regionList : any[] = [];
  normForm: FormGroup = this.fb.group({
    norms: this.fb.array([])
  });
  submissionFilterCtrl: FormControl = new FormControl();
  subscriptions: Subscription[] = [];
  submissionTypes: BasicLabManagerItemModel[] = [];
  isFilterBtnVisible: boolean = false;

  constructor(
      public matDialog: MatDialog,
      private router: Router,
      private seasonManagerService: SeasonManagerService,
      public nav: NavBarComponent,
      private seasonSharedDataService: SeasonSharedDataService,
      private pinManagerService: PinManagerService,
      private recommendationManagerService: RecommendationManagerService,
      private fb: FormBuilder,
      private recommendationManagerSharedDataService: RecommendationManagerSharedDataService,
      private labManagerService: LabManagerService,
      // Pin Management owns pin_samples, so the write goes there. Read is from
      // Recommendation Manager. Two services, but no new endpoint on the write side.
      private sampleService: SampleService,
      // The same service the field pages use, so both open this dialog with
      // identical data rather than two divergent assemblies of it.
      private fieldDetailsService: FieldDetailsService,
      private _snackBar: MatSnackBar,
  ) {

  }

  ngOnInit(): void {
    this.getMetaData();
    this.getSubmissionTypes();
    this.getSeasonDropdownDetails();

    this.subscriptions = [
        this.submissionFilterCtrl.valueChanges.subscribe({
          next: (res) => {
            this.filteredLabSubmissionList = [...this.labSubmissionList.filter(l => l.submissionId.toLowerCase().includes(res.toLowerCase()) || l.creationDate.toLowerCase().includes(res.toLowerCase()))];
          }
        }),

      this.clientFilterCtrl.valueChanges.subscribe({
        next: res => {
          this.filterCustomerList = [...this.customerList.filter(c => c?.name?.toLowerCase().includes(res.toLowerCase()))];
        }
      })
    ];

    this.getCustomerList();


    this.dataSource.filterPredicate = (data: InterpretationSampleRow, filter: string): boolean => {
      const dataStr = `
        ${data.farmName}
        ${data.fieldType}
        ${data.fieldName}
        ${data.cropDescription}
        ${data.labName}
        ${data.labTypeDescription}
        ${data.growthStage}
        ${data.fieldType}
      `.toLowerCase();

      return dataStr.includes(filter.trim().toLowerCase());
    }
  }

  /**
   * Reacts to the Analysis Type changing.
   *
   * Takes the chosen id from the event rather than reading selectedAnalysisTypeId,
   * which is written by the two-way binding and is not guaranteed to have been
   * updated by the time selectionChange fires. The template previously branched on
   * the field, so the first selection could still see null and take the soft tissue
   * path even when Soil had been chosen.
   */
  onAnalysisTypeChange(analysisTypeId: number): void {
    this.selectedAnalysisTypeId = analysisTypeId;

    // The soil type belongs to the previous analysis type. Its dropdown is hidden
    // for anything but Soil, so leaving the id set means a choice the user can no
    // longer see is still deciding what the page shows.
    this.selectedSoilTypeId = null;

    // So does the chosen submission. Soil and soft tissue are different lists from
    // different endpoints, so an id carried across either matches nothing and shows
    // blank, or matches a different submission entirely. The client picker already
    // clears this for the same reason.
    this.selectedLabSubmissionId = null;
    this.labSubmissionList = [];
    this.filteredLabSubmissionList = [];

    // And the grid, here rather than inside one of the two loaders below.
    // getSoilSubmissions happened to call resetValues and getLabSubmissionList did
    // not, so switching to Soft Tissue left the soil samples on screen under soft
    // tissue columns, with a selection that could still be submitted.
    this.resetValues(true);
    this.softTissueList = [];

    this.updateDisplayedColumns();
    this.loadRecManTypes();

    if (analysisTypeId === SOIL_ANALYSIS_TYPE) {
      this.getSoilSubmissions();
    } else {
      this.getLabSubmissionList();
    }
  }

  getLabSubmissionList() {
    // Guarded like getSoilSubmissions, which has always checked this. Without it the
    // service builds its params with clientId.toString() and throws on null, which it
    // did whenever an analysis type was chosen before a client.
    if (this.selectedClientId == null) {
      return;
    }

    // getSoftTissueListAll(true)
    this.nav.toggleLoading(true);
    this.subscriptions = [...this.subscriptions,
      this.recommendationManagerService.getLabSubmissionList(this.selectedClientId, this.selectedAnalysisTypeId).subscribe({
        next: res => {
          this.filteredLabSubmissionList = res;
          this.labSubmissionList = res;
          this.nav.toggleLoading(false);
        },
        error: () => {
          this.nav.toggleLoading(false);
        }
      })
    ];
  }

  getSubmissionTypes() {
    this.subscriptions = [...this.subscriptions,
      this.labManagerService.getSubmissionTypes().subscribe({
        next: res => {
          this.submissionTypes = res;
        }
      })
    ]
  }

  getSoftTissueList() {
    // this.subscriptions = [...this.subscriptions,
    //     this.recommendationManagerService.getSoftTissueList(this.currentPage, this.pageSize, this.startDate, this.endDate, this.searchText, this.selectedClientId).subscribe({
    //       next: (res) => {
    //         console.log(res)
    //       }
    //     })
    // ]
  }

  getCustomerList() {
    this.labManagerService.getCustomers().subscribe({
      next: res => {
        this.customerList = res;
        this.filterCustomerList = [...this.customerList];
        this.isLoadingCustomers = false;
      }
    })
  }

  get norms() {
    return this.normForm.controls['norms'] as FormArray;
  }

  addNorm(cropId: number) {
    this.norms.push(
        this.fb.group({
          crop: [{value: cropId, disabled: true}, Validators.required],
          region: ['', Validators.required],
          growthStage: ['', Validators.required]
        })
    );

    this.regionList.push({
      cropId: cropId,
      regions: [],
      growthStages: []
    })

    this.getRegionList(cropId, this.norms.length - 1);
  }

  removeNorm(index: number) {
    // let normLocation = this.norms.getRawValue().findIndex(n => n.crop === cropId);

    this.norms.removeAt(index);

    if(this.regionList?.length > 0) {
      this.regionList.splice(index, 1);
    }
  }

  filterDateRange() {
    if(this.startDate && this.endDate) {
      this.isFilterBtnVisible = true;
      this.filteringData = true;
      this.dataSource.data = this.softTissueList.filter(item => {
        const submissionDate = new Date(item.creationDate);
        return submissionDate >= this.startDate && submissionDate <= this.endDate;
      })
    } else {
      this.dataSource.data = this.softTissueList;
    }
  }

  clearFilters() {
    this.dataSource = new MatTableDataSource<InterpretationSampleRow>(this.softTissueList);
    this.startDate = null;
    this.endDate = null;
    this.searchText = '';
    this.isFilterBtnVisible = false;
    this.filteringData = false;
  }

  getMetaData() {

    this.subscriptions = [...this.subscriptions,
      this.recommendationManagerService.getCropMetaData().subscribe({
        next: (res) => {
          this.cropList = res;
        }
      })
    ];

  }

  getRegionList(cropId: number, index: number) {
    this.nav.toggleLoading(true);
    this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.getRegionsMetaData(cropId).subscribe({
          next: res => {

            this.regionList[index].regions = res;
            if(res?.length === 1) {
              (this.normForm.get('norms') as FormArray).at(index).get('region').setValue(res[0].id);
              this.getGrowthStageList(index, res[0].id);
            }
            
            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
    ]
  }

  getGrowthStageList(index: number, regionId: number) {
    this.nav.toggleLoading(true);
    let cropId = this.norms.controls[index].getRawValue()?.crop;

    this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.getGrowthStageMetaData(cropId, regionId).subscribe({
          next: res => {
            this.regionList[index].growthStages = res;
            
            if(res?.length === 1) {
              this.norms.controls[index]?.get('growthStage').setValue(res[0].normId);
            } else {
              this.norms.controls[index]?.get('growthStage').setValue(null);
            }

            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
    ]
  }

  selectAllSamples(event: any): void {
    this.dataSource.data.forEach(item => {
      this.selectSample(event, item);
    });

  }

  /**
   * Adds a norm row for the sample's crop, if it does not already have one.
   *
   * Skips unlinked samples deliberately. Their cropId is the server's COALESCE
   * fallback, guessed from the field's current-or-last season, not a crop this
   * sample belongs to. Adding a norm for it preselects a crop, and then
   * getRegionList and getGrowthStages preselect a region and growth stage on top,
   * so three values appear that the user never chose and that describe the wrong
   * crop. CREATE blocks on unlinked samples anyway, so nothing is lost by
   * waiting: onDefinitionSelected calls this again once the sample is linked.
   */
  addNewCrop(sample: InterpretationSampleRow) {
    if (this.isUnlinked(sample)) return;

    let newCropSelected = this.selectedSampleCrop.findIndex(s => s.cropId === sample?.cropId);
    if(sample?.cropId) {
      if(newCropSelected === -1) {
        this.addNorm(sample.cropId);
        this.selectedSampleCrop.push({
          cropId: sample.cropId,
          count: 1
        });
      } else {
        this.selectedSampleCrop[newCropSelected].count++;
      }

    }
  }

  /**
   * The full sentence behind a merge tag, shown on hover.
   *
   * The tag itself has to stay short enough to sit beside a sample name in a
   * dense table, so the consequence goes here rather than being left unsaid.
   */
  mergeTitle(sample: InterpretationSampleRow): string {
    return mergeHint(sample?.merge);
  }

  mergePanelOpen = false;
  mergePanelGroup: MergePanelGroup | null = null;

  /**
   * Opens the panel on a merged row.
   *
   * Only primaries are rows, so the clicked row always carries the whole group.
   */
  openMergePanel(sample: InterpretationSampleRow): void {
    const group = recommendationMergePanelGroup(sample as any);
    if (!group) {
      return;
    }
    this.mergePanelGroup = group;
    this.mergePanelOpen = true;
  }

  closeMergePanel(): void {
    this.mergePanelOpen = false;
    this.mergePanelGroup = null;
  }

  hasSample(sample: InterpretationSampleRow) {
    return this.selectedSamples.findIndex(s => s.id === sample.id) !== -1;
  }

  selectSample(event: any, sample: InterpretationSampleRow) {
    if (event.checked) {
      // Add to the array if checked
      if(this.selectedSamples.findIndex(s => s.id === sample.id) === -1) {
        this.selectedSamples.push(sample);
        this.addNewCrop(sample);
      }
    } else {
      // Remove from the array if unchecked
      const index = this.selectedSamples.findIndex(s => s.id === sample.id);
      if (index !== -1) {
        this.selectedSamples.splice(index, 1);
        this.removeCrop(sample);

      }
    }
  }

  /**
   * Must mirror addNewCrop's skip exactly.
   *
   * An unlinked sample never contributed a norm row, but its fallback cropId can
   * still match a LINKED sample's crop. Without this guard, deselecting an
   * unlinked sample would decrement a count it never incremented and could remove
   * a norm row another selected sample still needs.
   */
  removeCrop(sample: InterpretationSampleRow) {
    if (this.isUnlinked(sample)) return;

    let newCropSelected = this.selectedSampleCrop.findIndex(s => s.cropId === sample?.cropId);
    if(newCropSelected !== -1) {
      this.selectedSampleCrop[newCropSelected].count--;
      if(this.selectedSampleCrop[newCropSelected].count <= 0) {
        this.removeNorm(newCropSelected);
        this.selectedSampleCrop.splice(newCropSelected, 1);
      }
    }
  }

  openBackDialog() {
    const dialogRef = this.matDialog.open(DraftConfirmationDialogComponent, {
      data: {
        dialogType: 'back',
        dialogTitle: 'GO BACK?',
        dialogSubTitle: 'Unsaved changes will be lost, are you sure?'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if(res) {
        this.router.navigateByUrl('recommendation-manager');
      }
    })
  }


  getSeasonDropdownDetails() {
    this.seasonManagerService.getSeasonMetaData().subscribe({
      next: res => {
        if(res != null) {
          this.seasonSharedDataService.updateCombinedData(res);
        }
      }
    })
  }


  /**
   * Reload the grid after the field details dialog closes, whatever the outcome.
   *
   * Both branches call loadSubmissionDefinitions() themselves, so the crop
   * definition pickers repopulate as a side effect. That is the point of calling
   * this after a cancel too: the user may have attached the field to a season and
   * then closed without editing anything, and the new definition has to appear.
   *
   * The branch exists because getSoftTissueListAll is guarded by
   * selectedAnalysisTypeId === 1, so calling it on a soil submission silently did
   * nothing. Soil never refreshed after this dialog.
   */
  private refreshAfterFieldDetails(): void {
    if (this.selectedAnalysisTypeId === 2) this.loadSoilSamples();
    else this.getSoftTissueListAll(false);
  }

  /**
   * Opens the field details dialog through the shared service rather than
   * assembling it here.
   *
   * This screen used to hand-roll the whole flow: two loads counted into a shared
   * counter, the dialog opened when the counter reached two, and the result saved
   * inline. It was a worse copy of FieldDetailsService in five ways. It did not
   * synthesise a detail when the field had no season, so the dialog opened with
   * no field name and no field size. Its error handlers incremented the counter
   * without opening or closing anything, so a failed load left the loading
   * overlay up permanently. It never loaded the field-type extra detail, never
   * primed the shared data service, saved only the first definition on a
   * multi-cultivar field, and on a field with no season would have PUT to
   * /detail/undefined, because a synthesised detail has no id.
   *
   * take(1) because dialogClosed$ is a long-lived Subject on a root-provided
   * service: without it, every press of the button would leave another live
   * subscription behind.
   */
  openFieldDetailsDialog(sample: InterpretationSampleRow) {
    this.fieldDetailsService.viewFieldDetails(sample.fieldId, sample.tenantId)
      .pipe(take(1))
      .subscribe(() => this.refreshAfterFieldDetails());
  }

  filterTable() {
    this.dataSource.filter = this.searchText.trim().toLowerCase();

    if(this.searchText.length === 0 && !this.startDate && !this.endDate) {
      this.filteringData = false;
    } else {
      if(this.searchText.length > 0) {
        this.isFilterBtnVisible = true;
        this.filteringData = true;
      }
    }
  }

  loadRecManTypes(): void {
    if (this.selectedAnalysisTypeId) {
      this.recommendationManagerService.getRecManTypes(this.selectedAnalysisTypeId).subscribe({
        next: (res) => { this.soilTypes = res.filter(t => t.pinTypes?.name === 'Soil').map(t => ({ id: t.id, name: t.description })); }
      });
    }
  }

  getSoilSubmissions(): void {
    if (this.selectedClientId && this.selectedAnalysisTypeId != null) {
      //console.log("getSoilSubmissions", this.selectedClientId, this.selectedAnalysisTypeId);
      //console.log(this.selectedClientId, this.selectedAnalysisTypeId);
      this.resetValues(true);
      this.labSubmissionList = [];
      this.recommendationManagerService.getSoilSubmissions(this.selectedClientId, this.selectedAnalysisTypeId).subscribe({
        next: (res) => {
          this.labSubmissionList = res;
          this.filteredLabSubmissionList = res;
        }
      });
    }
  }

  loadSoilSamples(): void {
    if (this.selectedAnalysisTypeId === 2 && this.selectedLabSubmissionId) {
      this.nav.toggleLoading(true);

      // filteredLabSubmissionList is NOT cleared here, for the same reason it is not
      // cleared in getSoftTissueListAll: it holds the Lab References options, and
      // emptying it while loading the samples for a chosen reference left the control
      // with nothing matching the id it still held.
      this.recommendationManagerService.getSoilSamples(this.selectedLabSubmissionId).subscribe({
        next: (res) => {
          this.softTissueList = res;
          this.dataSource = new MatTableDataSource<InterpretationSampleRow>(res as any);
          this.loadSubmissionDefinitions();
          this.nav.toggleLoading(false);
        },
        error: () => { this.nav.toggleLoading(false); }
      });
    }
  }

  createSoilPreparation(): void {
    if (this.selectedSamples.length === 0) return;

    // Not for Soil Preparation. It interprets an analysis without reference to a
    // crop: the service only copies the link into a display DTO and never computes
    // with it. Maintenance is a different matter and keeps the gate, because it
    // reads the planted area through the link and silently falls back to the whole
    // field without it.
    //
    // This screen used to hold the gate for every soil type because it was the last
    // one every sample passed through, whether created on the web or on a phone, and
    // Soil Crop Nutrition needed the link captured somewhere. SCN no longer comes
    // through this screen at all: it is built from fields and seasons.
    const unlinked = needsCropDefinition(this.soilKind)
        ? this.selectedSamples.filter(s => this.isUnlinked(s))
        : [];
    if (unlinked.length) {
      const names = unlinked.map(s => s.sampleName || `#${s.pinSampleId}`).join(', ');
      this.openSnackBar(
        `Set a crop definition for ${unlinked.length === 1 ? 'this sample' : 'these samples'} first: ${names}`,
        'Dismiss');
      return;
    }

    // SCN does not come through here: its steps collect fields and seasons and the
    // embedded component posts them itself.
    if (this.isScn) {
      return;
    }

    if (needsStartingYear(this.soilKind)) {
      const dialogRef = this.matDialog.open(CalcSettingsDialogComponent, {
        width: "350px",
        data: { startingYear: new Date().getFullYear() }
      });
      dialogRef.afterClosed().subscribe(startingYear => {
        if (startingYear != null) {
          this.submitSoilCreation(startingYear);
        }
      });
    } else {
      this.submitSoilCreation(null);
    }
  }

  private submitSoilCreation(startingYear: number | null): void {
    const dto: any = {
      labManSubmissionId: this.selectedLabSubmissionId,
      recManTypesId: this.selectedSoilTypeId,
      customerId: this.selectedClientId,
      pinSamplesIds: this.selectedSamples.map(s => Number(s.pinSampleId || s.id))
    };
    if (startingYear != null) dto.startingYear = startingYear;
    this.nav.toggleLoading(true);
    this.recommendationManagerService.createSoilPreparation(dto).subscribe({
      next: (res) => {
        this.nav.toggleLoading(false);
        this.router.navigateByUrl(`${routeForKind(this.soilKind)}/${res.recId || res.id}`);
      },
      error: () => { this.nav.toggleLoading(false); }
    });
  }

  /**
   * Reacts to the Soil Type changing.
   *
   * Recomputes the columns as well as reloading, because which columns the soil grid
   * shows now depends on the soil type and not only on the analysis type.
   */
  onSoilTypeChange(soilTypeId: number): void {
    this.selectedSoilTypeId = soilTypeId;
    this.updateDisplayedColumns();

    // The submission list is keyed on the ANALYSIS type, not the soil type, so it is
    // the same list either side of this change and does not need fetching again. The
    // samples do. Switching used to call getSoilSubmissions, which cleared the grid
    // and then reloaded only the submissions, leaving the chosen reference sitting
    // above an empty table with no way back but reselecting it.
    if (this.selectedLabSubmissionId) {
      // The selection is dropped on purpose. Preparation and Maintenance do not
      // accept the same samples: an unlinked one is fine for Preparation and blocked
      // for Maintenance, so carrying a selection across would hand the user rows that
      // are quietly no longer valid.
      this.resetValues(true);
      this.loadSoilSamples();
    } else {
      this.getSoilSubmissions();
    }
  }

  updateDisplayedColumns(): void {
    if (this.selectedAnalysisTypeId !== SOIL_ANALYSIS_TYPE) {
      this.displayedColumns = this.softTissueColumns;
      return;
    }

    // Soil Preparation does not use the crop definition, so it does not ask for one.
    // Computed into a field rather than returned from a getter: the template binds
    // this and a getter would allocate a new array on every change detection pass.
    this.displayedColumns = needsCropDefinition(this.soilKind)
        ? this.soilColumns
        : this.soilColumns.filter(c => c !== 'definition');
  }

  /**
   * Whether to paint this row as needing attention.
   *
   * Being unlinked only counts where the link is actually wanted. For Soil
   * Preparation it is not: the column asking for it is not even rendered, so marking
   * the row red would flag a problem the user can neither see nor act on, for
   * something that does not affect the result.
   *
   * The missing-crop half stays soft tissue only, as it was.
   */
  rowHasError(row: InterpretationSampleRow): boolean {
    if (!row?.cropId && this.selectedAnalysisTypeId !== SOIL_ANALYSIS_TYPE) {
      return true;
    }
    return needsCropDefinition(this.soilKind) && this.isUnlinked(row);
  }

  /**
   * A sample with no crop definition of its own.
   *
   * Deliberately NOT based on cropId: the list query resolves crop and cultivar
   * through a fallback to the field's current-or-last season, so an unlinked
   * sample still shows a crop. That is exactly why this problem is invisible
   * today until CREATE returns a 422.
   */
  isUnlinked(row: InterpretationSampleRow): boolean {
    return !row?.seasonFieldDetailId;
  }

  /**
   * Why the crop and cultivar cannot be stated for this sample, or null when
   * they can be trusted.
   *
   * The list query resolves crop and cultivar through a COALESCE fallback to the
   * field's current-or-last season, so both columns are populated even for a
   * sample with no link at all. Rendering that as though it were the sample's
   * own crop is what made this problem invisible: the grid looked complete and
   * CREATE then failed with MISSING_CROP_LINK.
   *
   * Two different gaps, and the user can act on each differently. An unlinked
   * sample is fixed here, in the Crop Definition column. A definition missing
   * its cultivar has to be fixed in Season Management.
   */
  unknownReason(row: InterpretationSampleRow): string | null {
    if (this.isUnlinked(row)) {
      return 'Not linked to a crop definition. Any crop shown would be guessed from the field, '
           + 'so nothing is shown. Set the Crop Definition to resolve it.';
    }
    if (!row?.cultivar) {
      return 'Linked, but the crop definition has no cultivar set. Add one on the season.';
    }
    return null;
  }

  /**
   * Loads every crop definition for the chosen lab submission, in one call, and
   * builds the grouped structure the template renders.
   *
   * All the shaping happens here rather than in the template. Labels and groups
   * are computed once, so nothing allocates during change detection.
   */
  loadSubmissionDefinitions(): void {
    this.definitionGroupsByField = {};
    this.definitionsFailed = false;
    if (!this.selectedLabSubmissionId) return;

    this.definitionsLoading = true;
    this.recommendationManagerService.getSubmissionDefinitions(this.selectedLabSubmissionId).subscribe({
      next: (byField) => {
        this.definitionGroupsByField = this.buildDefinitionGroups(byField || {});
        this.definitionsLoading = false;
      },
      error: () => {
        this.definitionsLoading = false;
        this.definitionsFailed = true;
        this.openSnackBar('Could not load crop definitions for this submission', 'Dismiss');
      }
    });
  }

  /** Server response to render-ready groups, with every label already resolved. */
  private buildDefinitionGroups(byField: { [fieldId: string]: any[] }): { [fieldId: string]: DefinitionSeasonGroup[] } {
    const built: { [fieldId: string]: DefinitionSeasonGroup[] } = {};

    Object.keys(byField).forEach(fieldId => {
      const groups: DefinitionSeasonGroup[] = [];
      for (const def of byField[fieldId] || []) {
        let group = groups.find(g => g.seasonId === def.seasonId);
        if (!group) {
          group = {
            seasonId: def.seasonId,
            seasonName: def.seasonName || 'Unnamed season',
            // Every definition in a season shares its dates and state, so the
            // first one to create the group carries them.
            seasonState: def.seasonState || null,
            seasonStart: def.seasonStart || null,
            seasonEnd: def.seasonEnd || null,
            label: '',
            definitions: []
          };
          group.label = this.seasonGroupLabel(group);
          groups.push(group);
        }
        group.definitions.push({ ...def, label: this.definitionLabel(def) });
      }
      built[fieldId] = groups;
    });

    return built;
  }

  /** True once the load finished and this field genuinely has no definitions. */
  hasNoDefinitions(row: InterpretationSampleRow): boolean {
    if (this.definitionsLoading || this.definitionsFailed) return false;
    return !this.definitionGroupsByField[row?.fieldId]?.length;
  }

  /**
   * The season heading: its name, whether it is running, and the dates saying so.
   *
   * seasonState comes from the server rather than being worked out here, because
   * the database clock decides what has ended, not the machine the user is on.
   */
  seasonGroupLabel(group: DefinitionSeasonGroup): string {
    const parts: string[] = [group.seasonName];
    if (group.seasonState === 'running') parts.push('running now');
    else if (group.seasonState === 'upcoming') parts.push('not started');
    else if (group.seasonState === 'ended') parts.push('ended');

    const range = this.seasonDateRange(group);
    if (range) parts.push(range);
    return parts.join('  ·  ');
  }

  private seasonDateRange(group: DefinitionSeasonGroup): string | null {
    const start = this.asShortDate(group.seasonStart);
    const end = this.asShortDate(group.seasonEnd);
    if (!start && !end) return null;
    // An ended season only needs the date it ended. The rest is noise.
    if (group.seasonState === 'ended' && end) return end;
    if (start && end) return `${start} - ${end}`;
    return start || end;
  }

  private asShortDate(value: any): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /**
   * One option. Everything here exists to tell two cultivars on the same field
   * and season apart, which is the case the user is actually resolving.
   */
  definitionLabel(def: any): string {
    const cultivar = def?.cultivar || 'No cultivar';
    const crop = def?.cropDescription ? ` (${def.cropDescription})` : '';
    const size = def?.cultivarSize != null ? ` — ${truncateDecimals(def.cultivarSize, 2)} ha` : '';

    const extras: string[] = [];
    if (def?.fieldType) extras.push(def.fieldType);
    const planted = this.asShortDate(def?.plantDate);
    if (planted) extras.push(`planted ${planted}`);
    if (def?.isHarvested) extras.push('harvested');

    return `${cultivar}${crop}${size}${extras.length ? '  ·  ' + extras.join('  ·  ') : ''}`;
  }

  /**
   * Writes the link via Pin Management, then patches the row in place.
   *
   * Not a grid reload: that would discard the user's sample selection, and they
   * are usually part-way through choosing samples when they hit this.
   */
  onDefinitionSelected(row: InterpretationSampleRow, definitionId: number): void {
    if (!definitionId || !row) return;

    const sampleId = Number(row.pinSampleId);
    this.linkingSampleId = sampleId;

    this.sampleService.updateSampleLink(sampleId, definitionId).subscribe({
      next: () => {
        // reduce rather than flatMap: this project targets a TS lib older than es2019.
        const def = (this.definitionGroupsByField[row.fieldId] || [])
          .reduce((all: any[], g) => all.concat(g.definitions), [])
          .find(d => d.id === definitionId);
        row.seasonFieldDetailId = definitionId;
        row.linkedSeasonName = def?.seasonName ?? null;
        row.linkedCultivar = def?.cultivar ?? null;
        // Mirror the chosen definition exactly, including its blanks. Guarding
        // these assignments would leave the old fallback values on screen when
        // the definition has no cultivar, showing a cultivar the sample does not
        // have until the next reload.
        //
        // cropId matters beyond display: it is what addNewCrop builds the norm
        // row from. Until this point it held the server's fallback crop, guessed
        // from the field.
        if (def) {
          row.cropDescription = def.cropDescription ?? null;
          row.cultivar = def.cultivar ?? null;
          row.cropId = def.cropId ?? null;
        }

        // The sample now has a real crop, so it can have a norm. Only if it is
        // actually selected: linking a row the user has not ticked must not add
        // anything to the form at the bottom of the screen.
        if (this.hasSample(row)) this.addNewCrop(row);
        this.linkingSampleId = null;
        this.openSnackBar('Sample linked', 'Dismiss');
      },
      error: () => {
        this.linkingSampleId = null;
        this.openSnackBar('Could not link the sample. Please try again.', 'Dismiss');
      }
    });
  }

  getSoftTissueListAll(resetAll: boolean) {
    if(this.selectedAnalysisTypeId === 1 && this.selectedLabSubmissionId) {
      this.nav.toggleLoading(true);
      this.resetValues(resetAll);

      // filteredLabSubmissionList is NOT cleared here. It holds the options of the
      // Lab References dropdown, and this runs when the user has just picked one from
      // it: emptying it left the control with no option matching the id it still
      // held, so the selection appeared to vanish while its samples loaded below.
      this.subscriptions = [...this.subscriptions,
        this.recommendationManagerService.getSoftTissueListAll(this.selectedClientId, this.selectedLabSubmissionId).subscribe({
          next: res => {
            this.softTissueList = res;
            this.dataSource = new MatTableDataSource<InterpretationSampleRow>(res);
            this.loadSubmissionDefinitions();

            if(this.selectedSamples.length > 0) {

              for(let i = 0; i < this.selectedSamples.length; i++) {
                let index = res.findIndex(r => r.id === this.selectedSamples[i].id);

                if(index !== -1) {
                  this.selectedSamples[i]  = res[index];
                  this.addNewCrop(this.selectedSamples[i]);
                }
              }
            }
            this.nav.toggleLoading(false);
          },
          error: () => {
            this.nav.toggleLoading(false);
          }
        })
      ]
    }
  }

  resetValues(resetAll: boolean) {
    //console.log('Reset')
    this.dataSource = new MatTableDataSource<InterpretationSampleRow>();
    if(resetAll) {
      this.selectedSamples = [];
    }
    this.selectedSampleCrop = [];
    this.regionList = [];
    this.filteringData = false;
    this.norms.clear();
  }

  createInterpretation() {
    let hasIssue = !this.norms.valid || this.selectedSamples?.length === 0;

    if(!hasIssue) {
      for(let sample of this.selectedSamples) {
        if(!sample?.cropId) {
          hasIssue = true;
          break;
        }
      }
    }

    // Caught here rather than as a 422 from the server, which names one sample id
    // and gives the user nowhere to fix it.
    const unlinked = this.selectedSamples.filter(s => this.isUnlinked(s));

    if(hasIssue) {
      this.openSnackBar('Please ensure all fields are populated correctly and there are no issues with the crops', 'Dismiss');
    } else if(unlinked.length) {
      const names = unlinked.map(s => s.sampleName || `#${s.pinSampleId}`).join(', ');
      this.openSnackBar(
        `Set a crop definition for ${unlinked.length === 1 ? 'this sample' : 'these samples'} first: ${names}`,
        'Dismiss');
    } else {
      let sampleArray = [];

      if(this.selectedSamples.length > 0) {
        for(let sample of this.selectedSamples) {
          sampleArray.push({
            pinSampleId: sample.pinSampleId,
            normId: this.getNormIdInControl(sample.cropId)
          })
        }
      }

      let normData = {
        typeId: this.selectedAnalysisTypeId,
        customerId: this.selectedClientId,
        submissionId: this.selectedLabSubmissionId,
        samples: sampleArray
      }

      this.nav.toggleLoading(true);
      this.subscriptions = [...this.subscriptions,
          this.recommendationManagerService.createInterpretation(normData).subscribe({
            next: res => {
              this.router.navigateByUrl(`recommendation-manager/details/${res}`);
              this.nav.toggleLoading(false);
            },
            error: (err) => {
              this.nav.toggleLoading(false);
              if (err?.status === 422 && err?.error?.message?.includes('MISSING_CROP_LINK')) {
                // Names the crop definition, not the cultivar. Cultivar is optional
                // on a definition and is never what this check fails on.
                this.openSnackBar('Cannot generate recommendations: this sample is not linked to a crop definition. Set one in the Crop Definition column first.', 'Close');
              } else if (err?.status === 422) {
                this.openSnackBar(err?.error?.message || 'Cannot generate recommendations for this sample.', 'Close');
              } else {
                this.openSnackBar('Error creating recommendation. Please try again.', 'Close');
              }
            }
          })
      ]
    }
  }

  getNormIdInControl(cropId: number) {
    let normId = null;

    for(let control of this.norms.controls) {
      if(control.getRawValue().crop === cropId) {
        normId = control.value.growthStage;
        break;
      }
    }

    return normId;
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 7000,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    })
  }
}
